/**
 * Helpers to wipe user-scoped state across stores when switching accounts.
 *
 * Reset user-scoped in-memory stores when switching accounts.
 *
 * Business API data stores are intentionally not persisted anymore. The
 * `*-storage` names below are historical localStorage snapshots left by older
 * versions; we still remove them on logout / account switch so stale business
 * data cannot be rehydrated by an older build or a partially refreshed tab.
 */

import { useAppsStore } from '../modules/apps';
import { useDashboardStore } from '../modules/dashboard';
import { useCreditsStore } from '../modules/credits';
import { useDepositStore } from '../modules/deposit';
import { useSettingsStore } from '../modules/settings';
import { useCurrentSubscriptionStore } from '../modules/currentSubscription';
import { clearPersistedStore } from './persist';

const USER_SCOPED_STORAGE_KEYS = [
  'apps-storage',
  'dashboard-storage',
  'credits-storage',
  'deposit-storage',
  'settings-storage',
  'current-subscription-storage',
  'plans-storage',
] as const;

/**
 * Reset user-bound stores and remove their persisted snapshots.
 *
 * Keeps stores that are not business API data untouched (e.g. UI preferences,
 * deposit blockchain list, i18n settings). Subscription plan catalogue is also
 * reset elsewhere on demand because it is fetched from API and should not be
 * restored from localStorage.
 */
export function clearUserScopedStores(): void {
  useAppsStore.getState().clearApps();
  useDashboardStore.getState().clear();
  useCreditsStore.getState().clear();
  useDepositStore.getState().clear();
  useSettingsStore.getState().clear();
  useCurrentSubscriptionStore.getState().clear();

  USER_SCOPED_STORAGE_KEYS.forEach((name) => {
    clearPersistedStore(name);
  });
}
